import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingCart, Users, IndianRupee, FileText, Star, Mail, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { API, useAuth } from "@/App";

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getToken } = useAuth();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setAnalytics(res.data);
      } catch (err) {
        console.error("Failed to fetch analytics:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [getToken]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 animate-pulse rounded" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-200 animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) return <p className="text-gray-500">Failed to load dashboard.</p>;

  const stats = [
    { label: "Total Revenue", value: `₹${analytics.total_revenue?.toLocaleString() || 0}`, icon: IndianRupee, color: "bg-emerald-500", link: "/admin/orders" },
    { label: "Total Orders", value: analytics.total_orders, icon: ShoppingCart, color: "bg-blue-500", link: "/admin/orders" },
    { label: "Pending Orders", value: analytics.pending_orders, icon: TrendingUp, color: "bg-amber-500", link: "/admin/orders" },
    { label: "Total Users", value: analytics.total_users, icon: Users, color: "bg-violet-500", link: "/admin/users" },
    { label: "Products", value: analytics.total_products, icon: Package, color: "bg-teal-500", link: "/admin/products" },
    { label: "Blog Posts", value: analytics.total_blog_posts, icon: FileText, color: "bg-pink-500", link: "/admin/blog" },
    { label: "Testimonials", value: analytics.total_testimonials, icon: Star, color: "bg-orange-500", link: "/admin/testimonials" },
    { label: "Messages", value: analytics.total_contact_messages, icon: Mail, color: "bg-cyan-500", link: "/admin/messages" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Dashboard Overview</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Link key={s.label} to={s.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer border-gray-200" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`${s.color} w-10 h-10 rounded-lg flex items-center justify-center shrink-0`}>
                  <s.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                  <p className="text-xl font-bold text-gray-900">{s.value}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Orders */}
      <Card className="border-gray-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-gray-900">Recent Orders</h2>
            <Link to="/admin/orders" className="text-sm text-praroha-sage hover:underline">View All</Link>
          </div>
          {analytics.recent_orders?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="pb-2 font-medium text-gray-500">Order ID</th>
                    <th className="pb-2 font-medium text-gray-500">Total</th>
                    <th className="pb-2 font-medium text-gray-500">Status</th>
                    <th className="pb-2 font-medium text-gray-500">Payment</th>
                    <th className="pb-2 font-medium text-gray-500">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {analytics.recent_orders.map((order) => (
                    <tr key={order.id}>
                      <td className="py-2.5 font-mono text-xs text-gray-700">{order.id?.slice(0, 8)}...</td>
                      <td className="py-2.5 font-semibold">₹{order.total}</td>
                      <td className="py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          order.status === "confirmed" ? "bg-green-100 text-green-700" :
                          order.status === "pending" ? "bg-amber-100 text-amber-700" :
                          "bg-gray-100 text-gray-700"
                        }`}>{order.status}</span>
                      </td>
                      <td className="py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          order.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>{order.payment_status}</span>
                      </td>
                      <td className="py-2.5 text-gray-500 text-xs">{new Date(order.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No orders yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Users */}
      <Card className="border-gray-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-gray-900">Recent Users</h2>
            <Link to="/admin/users" className="text-sm text-praroha-sage hover:underline">View All</Link>
          </div>
          {analytics.recent_users?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="pb-2 font-medium text-gray-500">Name</th>
                    <th className="pb-2 font-medium text-gray-500">Email</th>
                    <th className="pb-2 font-medium text-gray-500">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {analytics.recent_users.map((u) => (
                    <tr key={u.id}>
                      <td className="py-2.5 font-medium text-gray-800">{u.name}</td>
                      <td className="py-2.5 text-gray-500">{u.email}</td>
                      <td className="py-2.5 text-gray-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No users yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
